import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

/**
 * Canonical production Home host that recipients are sent to when opening a shared chat.
 *
 * A share link is consumed by a recipient who has no relationship to the
 * tenant/origin where the chat was created (e.g. a chat shared from
 * `hello.atlassian.net` should still open on Home, not on `hello`). Production
 * tenants use this as the default for generated links.
 */
export const DEFAULT_SHARE_BASE_URL = 'https://home.atlassian.com';
export const STAGING_SHARE_BASE_URL = 'https://home.stg.atlassian.com';

/**
 * Path segment placed between the base URL and the opaque share token.
 */
const SHARE_PATH = '/chat/share';
const SHARE_PREVIEW_PATH = `${SHARE_PATH}/preview`;
const SITE_ID_BOOTSTRAP_EXPERIMENT = 'rovo_conversation_sharing_site_id_bootstrap';

export type BuildShareConversationPreviewUrlArgs = {
	conversationId: string;
	messageId: string;
	baseUrl?: string;
	orgId?: string;
	siteId?: string;
};

const getCurrentOrigin = (): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return window.location.origin;
};

/**
 * Extracts the organization id from the current URL when the page is scoped
 * through the Atlassian Home `/o/{orgId}` route segment.
 *
 * Share links are scoped to the sender's organization so Home can resolve the
 * snapshot from the correct org context. This reads the org id from the browser
 * location as a fallback for callers that do not already have an org id in
 * context (e.g. surfaces outside the chat that only know the site/cloud id).
 *
 * Returns `undefined` during SSR (no `window`) or when the path is not
 * org-scoped, in which case `buildShareConversationUrl` produces an unscoped
 * link.
 */
export const getOrgIdFromLocation = (): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	const match = window.location.pathname.match(/^\/o\/([^/]+)/);
	return match?.[1];
};

export const getDefaultShareBaseUrl = (origin: string | undefined = getCurrentOrigin()): string => {
	if (origin?.includes('jira-dev.com') || origin?.includes('home.stg.atlassian.com')) {
		return STAGING_SHARE_BASE_URL;
	}

	return DEFAULT_SHARE_BASE_URL;
};

const getShareURLSearchParams = (siteId?: string): URLSearchParams | undefined => {
	if (!isExperimentEnabled(SITE_ID_BOOTSTRAP_EXPERIMENT)) {
		return siteId ? new URLSearchParams({ siteId }) : undefined;
	}

	// The shared-chat flow consumes siteId, while Home bootstraps its tenant context from cloudId.
	// They identify the same site here, so include both to keep every consumer consistently scoped.
	return siteId ? new URLSearchParams({ siteId, cloudId: siteId }) : undefined;
};

/**
 * Builds the user-facing shareable URL for a chat snapshot from the
 * opaque `shareToken` returned by `PATCH /api/v2/share`.
 *
 * By default the link points at a Home host so that recipients are always
 * routed to the shared chat viewer regardless of which tenant/origin the
 * sender generated the link from. The current browser origin is only used to
 * choose between production Home and staging Home. When an `orgId` is supplied,
 * the link is scoped through the Atlassian Home organization path so Home can
 * resolve the share from the sender's organization context.
 *
 * An explicit `baseUrl` can be supplied to override the host — primarily so
 * staging/dev testers can open links against a specific non-production Home
 * host.
 *
 * @param shareToken - Opaque token identifying the share record.
 * @param baseUrl - Optional origin to build the link against (e.g. a staging
 *   Home host). Defaults to production Home, or staging Home when the current
 *   origin is a staging tenant or staging Home.
 * @param orgId - Optional organization id used to scope the Home route.
 * @param siteId - Optional site id used by Home to resolve the shared chat's
 *   tenant context.
 * @returns The full shareable URL to place on the clipboard / link field.
 */
export const buildShareConversationUrl = (
	shareToken: string,
	baseUrl: string = getDefaultShareBaseUrl(),
	orgId?: string,
	siteId?: string,
): string => {
	// Normalise away any trailing slash on the base URL so we never emit a
	// double slash before the share path.
	const normalisedBase = baseUrl.replace(/\/+$/, '');
	const orgPath = orgId ? `/o/${encodeURIComponent(orgId)}` : '';
	const params = getShareURLSearchParams(siteId);
	return `${normalisedBase}${orgPath}${SHARE_PATH}/${shareToken}${
		params ? `?${params.toString()}` : ''
	}`;
};

/**
 * Builds the sender preview URL for a chat snapshot without requiring a
 * share record to exist first. Home uses the conversation and message ids to
 * load the preview page through the preview API.
 */
export const buildShareConversationPreviewUrl = ({
	conversationId,
	messageId,
	baseUrl = getDefaultShareBaseUrl(),
	orgId,
	siteId,
}: BuildShareConversationPreviewUrlArgs): string => {
	const normalisedBase = baseUrl.replace(/\/+$/, '');
	const orgPath = orgId ? `/o/${encodeURIComponent(orgId)}` : '';
	const params = new URLSearchParams({
		conversationId,
		messageId,
		...(siteId ? { siteId } : {}),
	});
	return `${normalisedBase}${orgPath}${SHARE_PREVIEW_PATH}?${params.toString()}`;
};
